/**
 * FR-018.7 — the validity phrase of a Sources row (TENANT_CONFIGURATION_UI.md §2.1).
 *
 * The registry decides whether a kind is year-bound: the server sends `validity_kind`
 * (`season` | `global`) and the UI never infers it from the kind's name.
 */
import type { TFunction } from 'i18next';
import type { SourceRowDto } from '../../../../services/sourcesService';

type ValidityFields = Pick<SourceRowDto, 'valid_from' | 'valid_to' | 'validity_kind'>;
type Translate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

export function validityPhrase(row: ValidityFields, t: TFunction | Translate): string {
  const tr = t as Translate;
  const all = tr('farm.sources.validity.all', 'alle Jahre');
  if (row.validity_kind === 'global') return all;
  const { valid_from: from, valid_to: to } = row;
  if (from != null && to != null) {
    return from === to
      ? tr('farm.sources.validity.single', '{{year}}', { year: from })
      : tr('farm.sources.validity.range', '{{from}} – {{to}}', { from, to });
  }
  if (from != null) return tr('farm.sources.validity.from', 'ab {{year}}', { year: from });
  if (to != null) return tr('farm.sources.validity.until', 'bis {{year}}', { year: to });
  return all;
}

/** A season range that ended before the current year — rendered dimmed, listed after open ranges. */
export function isClosedRange(row: Pick<SourceRowDto, 'valid_to' | 'validity_kind'>, currentYear: number): boolean {
  return row.validity_kind === 'season' && row.valid_to != null && row.valid_to < currentYear;
}
